import React, { useEffect, useState, useMemo } from 'react';
import UserServices from '../services/UserServices';
import AdminServices from '../services/AdminServices';
import StateServices from '../services/StateServices';
import ViewUser from './View Model/viewThanaUser';
import Modal from 'react-modal';
import UserStatus from './TogelButton/UserStatus';
import UpdateProfile from './Update Model/UpdateThanaUser';
import ReactPaginate from 'react-paginate';
import * as XLSX from 'xlsx';
import Select, { components } from 'react-select';

Modal.setAppElement('#root');

function ThanaUser() {
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [userRole] = useState(() => localStorage.getItem('userRole'));
    const isPSHead = userRole === 'User';
    const isCOHead = userRole === 'CO';
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [search, setSearch] = useState('');
    const [activeIndex, setActiveIndex] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedEdit, setSelectedEdit] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [verifiedFilter, setVerifiedFilter] = useState('');
    const [districtFilter, setDistrictFilter] = useState('');
    const [policeStationFilter, setPoliceStationFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [districts, setDistricts] = useState([]);
    const [policeStations, setPoliceStations] = useState([]);
    const [data, setData] = useState(null);
    const [temp, setTemp] = useState(0);

    // Fetch logged-in user, then lock filters based on role
    useEffect(() => {
        const fetchLoggedInUser = async () => {
            try {
                const response = await AdminServices.getMyProfile();
                setLoggedInUser(response);

                if (response?.role === 'User') {
                    // PS Head: lock to their assigned police station only
                    const psId = response?.policeStation?._id;
                    const psName = response?.policeStation?.name;
                    if (psId) {
                        setPoliceStationFilter(psId);
                        setPoliceStations([{ _id: psId, name: psName }]);
                    }
                } else if (response?.role === 'CO') {
                    // Circle Head: load only police stations in their circle
                    const circleName = response?.circleName;
                    if (circleName) {
                        const thanaRes = await StateServices.getThanasByCircle(circleName);
                        const uniqueStations = (thanaRes.data || []).filter((s, i, self) =>
                            i === self.findIndex((x) => x._id === s._id)
                        );
                        setPoliceStations(uniqueStations);
                    }
                } else {
                    // Admin / others: load all districts and police stations
                    const [districtRes, thanaRes] = await Promise.all([
                        StateServices.getAllDistrict(),
                        StateServices.getAllThana(),
                    ]);
                    const uniqueDistricts = (districtRes.data || []).filter((d, i, self) =>
                        i === self.findIndex((x) => x._id === d._id)
                    );
                    const uniqueStations = (thanaRes.data || []).filter((s, i, self) =>
                        i === self.findIndex((x) => x._id === s._id)
                    );
                    setDistricts(uniqueDistricts);
                    setPoliceStations(uniqueStations);
                }
            } catch (error) {
                console.error('Error fetching logged-in user:', error);
            }
        };
        fetchLoggedInUser();
    }, []);

    // Reset to page 1 when filters change (except currentPage itself)
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [search, statusFilter, verifiedFilter, districtFilter, policeStationFilter, dateFilter]);

    // Fetch data from backend (no search — search is done on frontend)
    useEffect(() => {
        const limit = search ? 10000 : 50;
        const page = search ? 1 : currentPage;
        UserServices.getcircleThanaProfiles(page, statusFilter, verifiedFilter, districtFilter, policeStationFilter, dateFilter || undefined, limit).then((res) => {
            setData(res);
        }).catch((error) => {
            console.log(error);
        });
    }, [currentPage, search, statusFilter, verifiedFilter, districtFilter, policeStationFilter, dateFilter, temp]);

    const handleEditDetails = (thana) => {
        setSelectedEdit(thana);
        setIsEditModalOpen(true);
        toggleActionMenu(null);
    };
    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedEdit(null);
    };

    const handleViewDetails = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        // setSelectedUser(null);
    };

    const handlePageClick = (data) => {
        setCurrentPage(data.selected + 1);
    };

    const toggleActionMenu = (index) => {
        setActiveIndex(index === activeIndex ? null : index);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear().toString();
        return `${day}-${month}-${year}`;
    };

    const usersPerPage = 50;

    // Format districts for react-select (unique by _id)
    const districtOptions = useMemo(() => {
        const uniqueDistricts = districts.filter((district, index, self) =>
            index === self.findIndex((d) => d._id === district._id)
        );
        return uniqueDistricts.map(district => ({
            value: district._id,
            label: district.name
        }));
    }, [districts]);

    // Format police stations for react-select (unique by _id, filtered by district)
    const policeStationOptions = useMemo(() => {
        let filteredStations = policeStations;

        // Filter by district if selected
        if (districtFilter) {
            filteredStations = policeStations.filter((station) => {
                const stationDistrictId = station.district?._id || station.district;
                return stationDistrictId === districtFilter || stationDistrictId?.toString() === districtFilter;
            });
        }

        // Remove duplicates based on _id
        const uniqueStations = filteredStations.filter((station, index, self) =>
            index === self.findIndex((s) => s._id === station._id)
        );

        return uniqueStations.map(station => ({
            value: station._id,
            label: station.name
        }));
    }, [policeStations, districtFilter]);

    // Get selected values for react-select
    const selectedDistrict = useMemo(() =>
        districtOptions.find(opt => opt.value === districtFilter) || null,
        [districtOptions, districtFilter]
    );

    const selectedPoliceStation = useMemo(() =>
        policeStationOptions.find(opt => opt.value === policeStationFilter) || null,
        [policeStationOptions, policeStationFilter]
    );

    // Apply frontend search filter
    const allUsers = data?.data || [];
    const filteredUsers = search
        ? allUsers.filter((user) => {
            const term = search.toLowerCase();
            return (
                user?.name?.toLowerCase().includes(term) ||
                user?.mobileNo?.toLowerCase().includes(term) ||
                user?.aadharNo?.toLowerCase().includes(term) ||
                user?.verificationId?.toLowerCase().includes(term) ||
                user?.profession?.profession?.toLowerCase().includes(term) ||
                user?.user?.name?.toLowerCase().includes(term) ||
                user?.presentAddress?.state?.name?.toLowerCase().includes(term) ||
                user?.presentAddress?.district?.name?.toLowerCase().includes(term) ||
                user?.presentAddress?.policeStation?.name?.toLowerCase().includes(term)
            );
        })
        : allUsers;
    const count = search ? filteredUsers.length : (data?.totalProfiles || 0);

    // When searching, paginate filtered results on the frontend
    const displayUsers = search
        ? filteredUsers.slice((currentPage - 1) * usersPerPage, currentPage * usersPerPage)
        : filteredUsers;

    const generateExcel = () => {
        const filteredData = filteredUsers?.map((user, index) => ({
            "S.No": index + 1,
            UserType: user?.user?.name,
            UserMobileNo: user?.user?.mobileNo,
            Name: user?.name,
            MobileNo: user?.mobileNo,
            AadharNo: user?.aadharNo,
            State: user?.permanentAddress?.state?.name,
            District: user?.permanentAddress?.district?.name,
            PoliceStation: user?.permanentAddress?.policeStation?.name,
            Age: user?.age,
            Status: user?.status,
        }));

        const ws = XLSX.utils.json_to_sheet(filteredData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Users');

        XLSX.writeFile(wb, 'user_data.xlsx');
    };

    return (
        <div className="right_col" role="main">
            <div className="title-box">
                <h2>Satyapan <span className="badge bg-warning">{count}</span></h2>
                <button className="excel-btn" onClick={generateExcel}>
                    <img src="img/excel.svg" alt="Excel" />Download
                </button>
            </div>
            <div className="container-box px-0 user-manager">
                <div className="container-box-top-header px-4">
                    <div className="container-box-top-header-left-2">
                        <input
                            type="search"
                            name="search"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button className="search-btn">Search</button>

                        {/* Verified Filter Dropdown */}
                        <select
                            className="filter-dropdown"
                            value={verifiedFilter}
                            onChange={(e) => setVerifiedFilter(e.target.value)}
                        >
                            <option value="">All Verified</option>
                            <option value="Valid">Valid</option>
                            <option value="Invalid">Invalid</option>
                        </select>

                        {/* Status Filter Dropdown */}
                        <select
                            className="filter-dropdown"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="Verify">Verify</option>
                            <option value="UnVerify">UnVerify</option>
                        </select>

                        {/* Date Filter */}
                        <input
                            type="date"
                            className="filter-dropdown filter-date"
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                            style={{ height: '34px', borderRadius: '6px', border: '1px solid #E5E7EB' }}
                        />

                        {/* District Filter Dropdown - hidden for PS Head and Circle Head */}
                        {!isPSHead && !isCOHead && (
                        <div style={{ minWidth: '135px', flex: '0 0 135px', display: 'inline-block' }}>
                            <Select
                                value={selectedDistrict}
                                onChange={(selectedOption) => {
                                    setDistrictFilter(selectedOption ? selectedOption.value : '');
                                    setPoliceStationFilter('');
                                }}
                                options={districtOptions}
                                placeholder="All Districts"
                                isSearchable
                                isClearable
                                components={{
                                    Input: (props) => <components.Input {...props} placeholder={props.selectProps.menuIsOpen ? "Search districts..." : ""} />,
                                }}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '34px',
                                        height: '34px',
                                        fontSize: '13px',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        boxShadow: 'none',
                                        backgroundColor: '#fff',
                                        '&:hover': { border: '1px solid #D1D5DB' }
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        padding: '0 8px',
                                        height: '32px',
                                    }),
                                    input: (base) => ({
                                        ...base,
                                        margin: 0,
                                        padding: 0,
                                        color: '#333',
                                    }),
                                    placeholder: (base) => ({
                                        ...base,
                                        color: '#333',
                                        fontWeight: 500,
                                    }),
                                    singleValue: (base) => ({
                                        ...base,
                                        color: '#333',
                                        fontWeight: 500,
                                    }),
                                    indicatorSeparator: () => ({
                                        display: 'none',
                                    }),
                                    dropdownIndicator: (base) => ({
                                        ...base,
                                        padding: '0 6px',
                                        color: '#6B7280',
                                    }),
                                    clearIndicator: (base) => ({
                                        ...base,
                                        padding: '0 4px',
                                        color: '#9CA3AF',
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                        border: '1px solid #E5E7EB',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        borderRadius: '6px',
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        fontSize: '13px',
                                        padding: '8px 12px',
                                        backgroundColor: state.isSelected ? '#2563EB' : state.isFocused ? '#F3F4F6' : '#fff',
                                        color: state.isSelected ? '#fff' : '#333',
                                        cursor: 'pointer',
                                    }),
                                }}
                            />
                        </div>
                        )}

                        {/* Police Station Filter Dropdown */}
                        <div style={{ minWidth: '135px', flex: '0 0 135px', display: 'inline-block' }}>
                            <Select
                                value={selectedPoliceStation}
                                onChange={(selectedOption) => {
                                    if (!isPSHead) setPoliceStationFilter(selectedOption ? selectedOption.value : '');
                                }}
                                options={policeStationOptions}
                                placeholder="All Police Stations"
                                isSearchable={!isPSHead}
                                isClearable={!isPSHead}
                                isDisabled={isPSHead}
                                components={!isPSHead ? {
                                    Input: (props) => <components.Input {...props} placeholder={props.selectProps.menuIsOpen ? "Search police stations..." : ""} />,
                                } : undefined}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '34px',
                                        height: '34px',
                                        fontSize: '13px',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        boxShadow: 'none',
                                        backgroundColor: '#fff',
                                        '&:hover': { border: '1px solid #D1D5DB' }
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        padding: '0 8px',
                                        height: '32px',
                                    }),
                                    input: (base) => ({
                                        ...base,
                                        margin: 0,
                                        padding: 0,
                                        color: '#333',
                                    }),
                                    placeholder: (base) => ({
                                        ...base,
                                        color: '#333',
                                        fontWeight: 500,
                                    }),
                                    singleValue: (base) => ({
                                        ...base,
                                        color: '#333',
                                        fontWeight: 500,
                                    }),
                                    indicatorSeparator: () => ({
                                        display: 'none',
                                    }),
                                    dropdownIndicator: (base) => ({
                                        ...base,
                                        padding: '0 6px',
                                        color: '#6B7280',
                                    }),
                                    clearIndicator: (base) => ({
                                        ...base,
                                        padding: '0 4px',
                                        color: '#9CA3AF',
                                    }),
                                    menu: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                        border: '1px solid #E5E7EB',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        borderRadius: '6px',
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        fontSize: '13px',
                                        padding: '8px 12px',
                                        backgroundColor: state.isSelected ? '#2563EB' : state.isFocused ? '#F3F4F6' : '#fff',
                                        color: state.isSelected ? '#fff' : '#333',
                                        cursor: 'pointer',
                                    }),
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="container-box-inner">
                    <table id="example" className="table" style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>App User</th>
                                <th>Satyapan Name</th>
                                <th>Aadhar No.</th>
                                <th>Mobile No.</th>
                                <th>Profession</th>
                                <th>State</th>
                                <th>District</th>
                                <th>Police Station</th>
                                <th>Verification ID</th>
                                <th>Register Date</th>
                                {/* <th>Status Label</th> */}
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayUsers?.map((user, index) => (
                                <tr key={user?._id} className={user?.status === 'Verify' ? 'row-verified' : 'row-unverified'}>
                                    <td style={{ whiteSpace: 'nowrap', width: '40px' }}>
                                        {index + 1 + (currentPage - 1) * usersPerPage}
                                    </td>
                                    <td>
                                        <span>{user?.user?.name}</span>
                                    </td>
                                    <td>
                                        {user?.name}
                                    </td>
                                    <td>
                                        {user?.aadharNo}
                                    </td>
                                    <td>
                                        {user?.mobileNo}
                                    </td>
                                    <td>
                                        {user?.profession?.profession || 'N/A'}
                                    </td>
                                    <td>
                                        {user?.presentAddress?.state?.name || 'N/A'}
                                    </td>
                                    <td>
                                        {user?.presentAddress?.district?.name || 'N/A'}
                                    </td>
                                    <td>
                                        {user?.presentAddress?.policeStation?.name || 'N/A'}
                                    </td>
                                    <td>
                                        {[user?.presentAddress?.policeStation?.code, user?.verificationId, user?.presentAddress?.district?.code]
                                            .filter(Boolean).join(' ') || 'N/A'}
                                    </td>
                                    <td>
                                        {formatDate(user?.createdAt)}
                                    </td>
                                    <td className="status-toggle"><UserStatus user={user} page={"user"} /></td>
                                    <td>
                                        <div className="action_toggle_outer">
                                            <button
                                                className="action_toggle"
                                                onClick={() => toggleActionMenu(index)}
                                            >
                                                <i className="fa fa-ellipsis-v" />
                                            </button>
                                            {activeIndex === index && (
                                                <div className="action_toggle_content product-edit show d-block">
                                                    <ul className="list-unstyled">
                                                        <li>
                                                            <button className="edit" onClick={() => handleEditDetails(user)}>Edit</button>
                                                        </li>
                                                        <li>
                                                            <button className="view-details-btn" onClick={() => handleViewDetails(user)}>View</button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* <td>
                                        <button className="view-details-btn" onClick={() => handleViewDetails(user)}>
                                            View
                                        </button>
                                    </td> */}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="pagination">
                    <ReactPaginate
                        pageCount={Math.ceil(count / usersPerPage)}
                        pageRangeDisplayed={3}
                        marginPagesDisplayed={1}
                        onPageChange={handlePageClick}
                        containerClassName={'pagination'}
                        activeClassName={'active'}
                        previousLabel={'Previous'}
                        nextLabel={'Next'}
                        breakLabel={'...'}
                        breakClassName={'break-me'}
                        initialPage={currentPage - 1}
                        pageLinkClassName={'page-link'}
                        previousLinkClassName={'page-link'}
                        nextLinkClassName={'page-link'}
                        pageClassName={'page-item'}
                        previousClassName={'page-item'}
                        nextClassName={'page-item'}
                        disabledClassName={'disabled'}
                    />
                </div>
            </div>

            <Modal
                isOpen={isEditModalOpen}
                onRequestClose={closeEditModal}
                contentLabel="Edit Category"
                className="modal-content"
                overlayClassName="modal-overlay"
            >
                <UpdateProfile selectedData={selectedEdit} closeModal={closeEditModal} onSuccess={() => setTemp(temp + 1)} />
            </Modal>
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="User Details"
                className="modal-content"
                overlayClassName="modal-overlay"
            >
                <ViewUser user={selectedUser} closeModal={closeModal} />
            </Modal>
        </div>
    );
}

export default ThanaUser;